import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { SideBarModal } from '../Modal';
import { Button } from '../Button';
import { showErrorToast } from '../toast/globalToast';

export type LabelLayout = 'THERMAL_4X6' | 'THERMAL_100X100';

export interface PrintLabelModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Value encoded into the QR code (e.g. vehicle ID) */
  qrValue: string;
  /** Text printed below the QR code (e.g. plate number) */
  labelText: string;
  className?: string;
}

const LOGO_PATH = '/images/dash-electric.png';

// Same layout catalogue as react-logistic-web's label modal: thermal full
// (100×150mm, 4×6 in) and thermal square (100×100mm). Sizes in mm drive the
// print @page; the on-screen preview scales them at 3px per mm.
const LAYOUTS: Record<
  LabelLayout,
  { name: string; widthMm: number; heightMm: number }
> = {
  THERMAL_4X6: { name: 'Thermal full', widthMm: 100, heightMm: 150 },
  THERMAL_100X100: { name: 'Thermal 100×100', widthMm: 100, heightMm: 100 },
};
const PREVIEW_PX_PER_MM = 3;
// Download renders the label to a PNG at print resolution (~203dpi thermal).
const PNG_PX_PER_MM = 8;
const QR_SIZE_MM = 45;

// Render the QR code to a data URL so both the on-screen preview and the
// print document embed the exact same image.
const buildQrDataUrl = (value: string): Promise<string> =>
  QRCode.toDataURL(value, {
    errorCorrectionLevel: 'M',
    margin: 1,
    width: 216,
  });

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });

// Draw the label onto a canvas at print resolution and return it as a PNG
// blob. Mirrors the print document's layout (same mm dimensions and spacing).
const buildLabelPng = async (
  qrDataUrl: string,
  labelText: string,
  layout: LabelLayout
): Promise<Blob> => {
  const { widthMm, heightMm } = LAYOUTS[layout];
  const S = PNG_PX_PER_MM;
  const [logo, qr] = await Promise.all([
    loadImage(LOGO_PATH),
    loadImage(qrDataUrl),
  ]);

  const canvas = document.createElement('canvas');
  canvas.width = widthMm * S;
  canvas.height = heightMm * S;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas is not supported');

  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#000';

  const pad = 6 * S;
  const cx = canvas.width / 2;

  // Header — logo + brand centered as one group
  const logoSize = 8 * S;
  const gap = 2.5 * S;
  const brand = 'Dash Electric Fleet';
  ctx.font = `700 ${5.5 * S}px 'Plus Jakarta Sans', Arial, sans-serif`;
  ctx.textBaseline = 'middle';
  const groupW = logoSize + gap + ctx.measureText(brand).width;
  ctx.drawImage(logo, cx - groupW / 2, pad, logoSize, logoSize);
  ctx.textAlign = 'left';
  ctx.fillText(brand, cx - groupW / 2 + logoSize + gap, pad + logoSize / 2);

  // Header divider
  const lineY = pad + logoSize + 3 * S;
  ctx.fillRect(pad, lineY, canvas.width - pad * 2, Math.max(1, 0.35 * S));

  // Content — QR + plate centered in the remaining space
  const qrSize = QR_SIZE_MM * S;
  const plateFont = 8 * S;
  const contentGap = 3 * S;
  const groupH = qrSize + contentGap + plateFont;
  const groupTop = lineY + (canvas.height - pad - lineY - groupH) / 2;
  ctx.imageSmoothingEnabled = false; // keep QR modules crisp when scaling
  ctx.drawImage(qr, cx - qrSize / 2, groupTop, qrSize, qrSize);
  ctx.font = `700 ${plateFont}px 'Plus Jakarta Sans', Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillText(labelText, cx, groupTop + qrSize + contentGap + plateFont / 2);

  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) =>
        blob ? resolve(blob) : reject(new Error('Failed to render PNG')),
      'image/png'
    )
  );
};

// Self-contained HTML document for the hidden print iframe, sized to the
// thermal label via @page. The logo needs an absolute URL because the
// document is served from a blob: URL, where relative paths don't resolve.
const buildLabelHtml = (
  qrDataUrl: string,
  labelText: string,
  layout: LabelLayout
): string => {
  const { widthMm, heightMm } = LAYOUTS[layout];
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>Label ${labelText}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  @page { size: ${widthMm}mm ${heightMm}mm; margin: 0; }
  html, body { width: ${widthMm}mm; height: ${heightMm}mm; }
  body { font-family: 'Plus Jakarta Sans', Arial, sans-serif; background: #fff; color: #000; }
  .label { width: 100%; height: 100%; padding: 6mm; display: flex; flex-direction: column; text-align: center; }
  .header { display: flex; align-items: center; justify-content: center; gap: 2.5mm; padding-bottom: 3mm; border-bottom: 1px solid #000; }
  .header img { width: 8mm; height: 8mm; }
  .header .brand { font-size: 5.5mm; font-weight: 700; }
  .content { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 3mm; }
  .qr { width: ${QR_SIZE_MM}mm; height: ${QR_SIZE_MM}mm; }
  .plate { font-size: 8mm; font-weight: 700; letter-spacing: 0.5mm; }
</style>
</head>
<body>
  <div class="label">
    <div class="header">
      <img src="${window.location.origin}${LOGO_PATH}" alt="Dash" />
      <span class="brand">Dash Electric Fleet</span>
    </div>
    <div class="content">
      <img class="qr" src="${qrDataUrl}" alt="QR Code" />
      <div class="plate">${labelText}</div>
    </div>
  </div>
</body>
</html>`;
};

export const PrintLabelModal: React.FC<PrintLabelModalProps> = ({
  isOpen,
  onClose,
  qrValue,
  labelText,
  className = '',
}) => {
  const [layout, setLayout] = useState<LabelLayout>('THERMAL_4X6');
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (!isOpen || !qrValue) {
      setQrDataUrl(null);
      return;
    }
    let cancelled = false;
    buildQrDataUrl(qrValue)
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) showErrorToast('Error', 'Failed to generate QR code');
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, qrValue]);

  const handleDownload = async () => {
    if (!qrDataUrl) return;
    setIsDownloading(true);
    try {
      const blob = await buildLabelPng(qrDataUrl, labelText, layout);
      const { widthMm, heightMm } = LAYOUTS[layout];
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `label-${labelText.replace(/\s+/g, '-')}-${widthMm}x${heightMm}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showErrorToast('Error', 'Failed to download label');
    } finally {
      setIsDownloading(false);
    }
  };

  const handlePrint = () => {
    if (!qrDataUrl) return;

    const html = buildLabelHtml(qrDataUrl, labelText, layout);
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const iframe = document.createElement('iframe');
    iframe.style.cssText =
      'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
    iframe.src = url;
    iframe.onload = () => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      // Keep the iframe alive while the print dialog is open; cleaning up
      // immediately would blank the print preview in some browsers.
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
      }, 60000);
    };
    document.body.appendChild(iframe);
  };

  const { widthMm, heightMm } = LAYOUTS[layout];
  const previewWidth = widthMm * PREVIEW_PX_PER_MM;
  const previewHeight = heightMm * PREVIEW_PX_PER_MM;
  const qrPreviewSize = QR_SIZE_MM * PREVIEW_PX_PER_MM;

  return (
    <SideBarModal
      isOpen={isOpen}
      onClose={onClose}
      width="sm"
      position="center"
      className={className}
      header={
        <div>
          <h2 className="text-[18px] font-semibold text-gray-900">
            Print Label
          </h2>
          <p className="text-[13px] text-gray-500">
            {`Label ${widthMm}×${heightMm} mm`}
          </p>
        </div>
      }
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="white" size="small" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="white"
            size="small"
            onClick={handleDownload}
            disabled={!qrDataUrl || isDownloading}
          >
            <i
              className={`text-[12px] mr-1 ${
                isDownloading
                  ? 'ri-loader-4-line animate-spin'
                  : 'ri-download-2-line'
              }`}
            ></i>
            Download
          </Button>
          <Button
            variant="primary"
            size="small"
            onClick={handlePrint}
            disabled={!qrDataUrl}
          >
            <i className="ri-printer-line text-[12px] mr-1"></i>
            Print
          </Button>
        </div>
      }
    >
      <div className="space-y-4 py-2">
        {/* Layout toggle */}
        <div className="mx-auto flex w-fit rounded-lg bg-gray-100 p-1">
          {(Object.keys(LAYOUTS) as LabelLayout[]).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setLayout(key)}
              className={`rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors ${
                layout === key
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {LAYOUTS[key].name}
            </button>
          ))}
        </div>

        {/* Label preview — same proportions as the printed label */}
        <div className="flex justify-center">
          <div
            className="flex flex-col rounded border border-gray-900 p-[18px] text-center"
            style={{ width: previewWidth, height: previewHeight }}
          >
            <div className="flex items-center justify-center gap-2 border-b border-gray-900 pb-2">
              <img src={LOGO_PATH} alt="Dash" className="h-6 w-6" />
              <span className="text-[17px] font-bold text-gray-900">
                Dash Electric Fleet
              </span>
            </div>
            <div className="flex flex-1 flex-col items-center justify-center gap-2">
              {qrDataUrl ? (
                <img
                  src={qrDataUrl}
                  alt={`QR Code ${qrValue}`}
                  style={{ width: qrPreviewSize, height: qrPreviewSize }}
                />
              ) : (
                <div className="text-[14px] text-gray-400">
                  Generating QR code...
                </div>
              )}
              <div className="text-[24px] font-bold tracking-wide text-gray-900">
                {labelText}
              </div>
            </div>
          </div>
        </div>
      </div>
    </SideBarModal>
  );
};

export default PrintLabelModal;
